import EditIcon from "@mui/icons-material/Edit";
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import GlobalAudienceFloorsDropdownForm from "../features/audiences/./global_floors/globalAudienceFloorsDropdownForm";
import GlobalAudienceEditActionStatus from "../features/audiences/global_floors/globalAudienceEditActionStatus";
import GlobalAudienceFloorsTable from "../features/audiences/global_floors/globalAudienceFloorsTable";
import {
  editPricingButtonStyle,
  GlobalAudienceFloorsInstructions,
  globalFloorsFetcher,
  NoMaxWidthTooltip,
  preview_floors_changes,
  TableSpacing,
  textFieldChanged,
  textFieldError,
  textFieldNormal,
  update,
} from "../features/audiences/global_floors/utils/globalAudienceFloorsUtils";
import useTitle from "../hooks/useTitle";

const GlobalAudienceFloors = () => {
  useTitle("M+ Global Pricing");
  const [currentValuesInDB, setCurrentValuesInDB] = useState({});
  const [dataRevenueShareInput, setDataRevenueShareInput] = useState("");
  const [segmentCostInput, setSegmentCostInput] = useState("");
  const [cpsFloors, setCpsFloors] = useState({});
  const [reserveCosts, setReserveCosts] = useState({});
  const [editPricingVariables, setEditPricingVariables] = useState(false);
  const [errorPricing, setErrorPricing] = useState({});
  const [isClear, setIsClear] = useState(false);
  const [editingAlert, setEditingAlert] = useState("edit");
  const [showEditingAlert, setShowEditingAlert] = useState(false);
  const [dropDownParams, setDropDownParams] = useState({});
  const [isRecalculateCPC, setIsRecalculateCPC] = useState(false);
  const [isRecalculateReserve, setIsRecalculateReserve] = useState(false);
  const [dataRevenueShareInputStyle, setDataRevenueShareInputStyle] =
    useState(textFieldNormal);
  const [segmentShareInputStyle, setSegmentShareInputStyle] =
    useState(textFieldNormal);

  const changedHighlightOff = () => {
    setDataRevenueShareInputStyle(textFieldNormal);
    setSegmentShareInputStyle(textFieldNormal);
    setIsRecalculateReserve(false);
    setIsRecalculateCPC(false);
  };

  const clearTable = () => {
    setCpsFloors({});
    setReserveCosts({});
    setCurrentValuesInDB({});
  };

  const validatePricingVariables = () => {
    const shareError =
      dataRevenueShareInput === "" ||
      !/^([0-9]{1,2}(\.[0-9]{0,2})?)$|^99(\.0{0,2})?$/.test(
        dataRevenueShareInput
      );
    const segmentError =
      segmentCostInput === "" ||
      !/^([0-9]{1,2}(\.[0-9]{0,2})?)$|^10(\.0{0,2})?$/.test(segmentCostInput);
    setErrorPricing((prevError) => ({
      ...prevError,
      share: shareError,
      segment: segmentError,
    }));
    return !(
      shareError ||
      segmentError ||
      dataRevenueShareInput === "" ||
      segmentCostInput === ""
    );
  };

  const previewFloorChanges = () => {
    if (!validatePricingVariables()) {
      return;
    }
    const params = {
      ...dropDownParams,
      data_revenue_share: Number(dataRevenueShareInput) / 100,
      cost_in_cents: Number(segmentCostInput) * 100,
    };
    preview_floors_changes(params).then((result) => {
      recalculateValues(result);
    });
  };

  const recalculateValues = (result) => {
    changedHighlightOff();
    if (
      Number(result["data_revenue_share"]) !==
      Number(currentValuesInDB["data_revenue_share"])
    ) {
      setCpsFloors(result["audience_floors"]);
      setIsRecalculateCPC(true);
      setDataRevenueShareInputStyle(textFieldChanged);
    }
    if (
      Number(result["cost_in_cents"]) !==
      Number(currentValuesInDB["cost_in_cents"])
    ) {
      setReserveCosts(result["data_reserve_costs"]);
      setCpsFloors(result["audience_floors"]);
      setIsRecalculateReserve(true);
      setIsRecalculateCPC(true);
      setSegmentShareInputStyle(textFieldChanged);
    }
  };

  const cancelUpdate = () => {
    changedHighlightOff();
    setErrorPricing({});
    setIsRecalculateReserve(false);
    setIsRecalculateCPC(false);
    setEditPricingVariables(!editPricingVariables);
    setEditingAlert("cancel");
    setCpsFloors(currentValuesInDB["audience_floors"]);
    setReserveCosts(currentValuesInDB["data_reserve_costs"]);
    setDataRevenueShareInput(currentValuesInDB["data_revenue_share"] * 100);
    setSegmentCostInput(currentValuesInDB["cost_in_cents"] / 100);
  };

  const submitPricingVariables = () => {
    if (!validatePricingVariables()) {
      return;
    }
    const params = {
      ...dropDownParams,
      data_revenue_share: Number(dataRevenueShareInput) / 100,
      cost_in_cents: Number(segmentCostInput) * 100,
    };
    update(params).then((result) => {
      recalculateValues(result);
      setEditPricingVariables(false);
      setEditingAlert("updated");
      setCurrentValuesInDB(result);
      changedHighlightOff();
    });
  };

  const enableUpdate = () => {
    setEditingAlert("edit");
    setShowEditingAlert(true);
    setEditPricingVariables(true);
  };

  const clearButtonClicked = () => {
    setIsClear(false);
    setDataRevenueShareInput("");
    setSegmentCostInput("");
  };

  const handleApply = (params) => {
    setDropDownParams(params);
    globalFloorsFetcher(params).then((result) => {
      setCurrentValuesInDB(result);
      setCpsFloors(result["audience_floors"]);
      setReserveCosts(result["data_reserve_costs"]);
      setDataRevenueShareInput(result["data_revenue_share"] * 100);
      setSegmentCostInput(result["cost_in_cents"] / 100);
      setIsClear(true);
    });
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
          <GlobalAudienceFloorsInstructions />
          <br />
          <GlobalAudienceFloorsDropdownForm
            submitMethod={handleApply}
            clearTable={clearTable}
            editPricingVariables={editPricingVariables}
            setClear={clearButtonClicked}
          />
          <br />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TableContainer>
                <Table size="small">
                  <GlobalAudienceFloorsTable
                    isCPC={true}
                    floorData={cpsFloors}
                    isRecalculate={isRecalculateCPC}
                  />
                  <TableSpacing />
                  <GlobalAudienceFloorsTable
                    isCPC={false}
                    floorData={reserveCosts}
                    isRecalculate={isRecalculateReserve}
                  />
                </Table>
              </TableContainer>
            </Grid>
            {editPricingVariables && (
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ width: 112, height: 30 }}
                  onClick={previewFloorChanges}
                >
                  Recalculate
                </Button>
              </Grid>
            )}
          </Grid>
          <br />
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="h6">Pricing Variables</Typography>
              </Grid>
              <Grid item xs={6} style={{ textAlign: "right" }}>
                {!editPricingVariables && isClear && (
                  <Button
                    variant="contained"
                    color="primary"
                    style={{
                      width: 112,
                      height: 36,
                      paddingLeft: 10,
                      paddingRight: 10,
                    }}
                    onClick={enableUpdate}
                  >
                    Update
                  </Button>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                style={{ display: "flex", alignItems: "center" }}
              >
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <NoMaxWidthTooltip
                          title="Margin on Audience cost used to calculate Data Revenue"
                          arrow
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component={"div"}
                            sx={{ fontSize: "1rem" }}
                          >
                            Data Revenue Share
                          </Typography>
                        </NoMaxWidthTooltip>
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingLeft: "20px",
                          }}
                        >
                          {editPricingVariables && (
                            <EditIcon
                              sx={{
                                marginLeft: 3,
                                marginRight: 1,
                                color: "gray",
                                size: "big",
                              }}
                            />
                          )}
                          <FormControl
                            margin="normal"
                            style={{ flex: 1 }}
                            fullWidth
                          >
                            <TextField
                              sx={
                                errorPricing["share"]
                                  ? textFieldError
                                  : dataRevenueShareInputStyle
                              }
                              value={dataRevenueShareInput}
                              onChange={(e) => {
                                const value = e.target.value;

                                const validValue =
                                  /^([0-9]{1,2}(\.[0-9]{0,2})?)$|^99$/.test(
                                    value
                                  );
                                if (value === "99.") return;
                                if (validValue || value === "") {
                                  setDataRevenueShareInput(value);
                                }
                              }}
                              InputProps={{
                                sx: { textAlign: "right" },
                                readOnly: !editPricingVariables,
                                endAdornment: (
                                  <InputAdornment
                                    sx={{
                                      margin: -1.5,
                                      paddingRight: "0px",
                                      paddingLeft: "0px",
                                    }}
                                    position="end"
                                  >
                                    <span>%</span>
                                  </InputAdornment>
                                ),
                              }}
                              inputProps={{
                                sx: {
                                  textAlign: "center",
                                  "&::placeholder": {
                                    textAlign: "center", // Ensure placeholder text is also right-aligned
                                  },
                                  paddingRight: "12px",
                                  paddingLeft: "0px",
                                  paddingTop: "7px",
                                  paddingBottom: "7px",
                                  fontSize: "14px",
                                },
                              }}
                            />
                            {editPricingVariables && (
                              <FormHelperText>
                                Do not enter values exceeding 99%
                              </FormHelperText>
                            )}
                          </FormControl>
                        </div>
                      </td>
                      <td>
                        {errorPricing["share"] && editPricingVariables && (
                          <Typography
                            variant={"Body 1"}
                            style={{ color: "rgba(181, 25, 27, 1)" }}
                          >
                            Enter a number between 0 and 99, with up to two
                            decimal places if required.
                          </Typography>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <NoMaxWidthTooltip
                          title="Maximum cost of a Segment within the Audience"
                          arrow
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component={"div"}
                            sx={{ fontSize: "1rem" }}
                          >
                            Segment Cost (CPM)
                          </Typography>
                        </NoMaxWidthTooltip>
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingLeft: "20px",
                          }}
                        >
                          {editPricingVariables && (
                            <EditIcon
                              sx={{
                                marginLeft: 3,
                                marginRight: 1,
                                color: "gray",
                                size: "big",
                              }}
                            />
                          )}
                          <TextField
                            fullWidth
                            value={segmentCostInput}
                            onChange={(e) => {
                              const value = e.target.value;

                              const validValue =
                                /^([0-9]{1}(\.[0-9]{0,2})?)$|^10$/.test(value);
                              if (validValue || value === "") {
                                setSegmentCostInput(value);
                              }
                            }}
                            InputProps={{
                              readOnly: !editPricingVariables,
                              startAdornment: (
                                <InputAdornment
                                  sx={{ margin: -1.2 }}
                                  position="start"
                                >
                                  <span>$</span>
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{
                              sx: {
                                textAlign: "center",
                                "&::placeholder": {
                                  textAlign: "center", // Ensure placeholder text is also right-aligned
                                },
                                paddingRight: "12px",
                                paddingLeft: "12px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                                fontSize: "14px",
                              },
                            }}
                            sx={
                              errorPricing["segment"]
                                ? textFieldError
                                : segmentShareInputStyle
                            }
                          />
                        </div>
                      </td>
                      <td>
                        {errorPricing["segment"] && editPricingVariables && (
                          <Typography
                            variant={"Body 1"}
                            style={{ color: "rgba(181, 25, 27, 1)" }}
                          >
                            Enter a number between 0 and $10, with up to two
                            decimal places if required.
                          </Typography>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Grid>
              {editPricingVariables && (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    style={editPricingButtonStyle}
                    onClick={submitPricingVariables}
                  >
                    Confirm
                  </Button>
                  &nbsp;&nbsp;
                  <Button
                    variant="contained"
                    style={editPricingButtonStyle}
                    onClick={cancelUpdate}
                  >
                    Cancel
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
          {showEditingAlert && (
            <GlobalAudienceEditActionStatus
              editingAlert={editingAlert}
              setShowEditingAlert={setShowEditingAlert}
            />
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default GlobalAudienceFloors;
